import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHero from "@/components/PageHero";
import { MapPin, Mail, Phone, Send, CheckCircle2 } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1400&q=80";

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", subject: "", message: "", inquiry_type: "general",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We'll reply within one business day.");
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title={<>Let&apos;s start <span className="italic" style={{ color: "var(--accent-teal)" }}>a conversation.</span></>}
        subtitle="Reach us any way you like. Forms go to a senior consultant — not a shared inbox. We respond within one business day, every time."
        image={HERO_IMG}
        imageAlt="Modern Vancouver office"
        testId="contact-hero"
      />

      <section className="py-16 md:py-24" data-testid="contact-section">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 grid md:grid-cols-12 gap-10">
          <aside className="md:col-span-5 space-y-6">
            <div className="card" data-testid="contact-info-card">
              <div className="eyebrow mb-3">Vancouver HQ</div>
              <h3 className="font-display text-2xl mb-5">Ashren Consulting Ltd.</h3>

              <ul className="space-y-5 text-[color:var(--primary-dark)]">
                <li className="flex gap-4" data-testid="contact-address">
                  <div className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center"
                       style={{ background: "rgba(245,166,35,0.15)", color: "var(--accent-amber)" }}>
                    <MapPin size={18} />
                  </div>
                  <div>
                    <div className="font-semibold">Office</div>
                    <div className="text-[color:var(--muted)] text-sm mt-1">
                      101, 2529 Kingsway<br />
                      Vancouver, BC V5R 5H3<br />
                      Canada
                    </div>
                  </div>
                </li>
                <li className="flex gap-4" data-testid="contact-email">
                  <div className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center"
                       style={{ background: "rgba(29,180,222,0.15)", color: "var(--accent-teal)" }}>
                    <Mail size={18} />
                  </div>
                  <div>
                    <div className="font-semibold">Email</div>
                    <a href="mailto:Sales@ashrenltd.com" className="text-[color:var(--muted)] text-sm mt-1 block hover:text-[color:var(--primary-dark)]">Sales@ashrenltd.com</a>
                  </div>
                </li>
                <li className="flex gap-4" data-testid="contact-phone">
                  <div className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center"
                       style={{ background: "rgba(108,91,232,0.15)", color: "var(--accent-purple)" }}>
                    <Phone size={18} />
                  </div>
                  <div>
                    <div className="font-semibold">Phone</div>
                    <a href="tel:+12364850134" className="text-[color:var(--muted)] text-sm mt-1 block hover:text-[color:var(--primary-dark)]">+1 236-485-0134</a>
                  </div>
                </li>
              </ul>
            </div>
          </aside>

          <div className="md:col-span-7">
            {submitted ? (
              <div className="card text-center py-16" data-testid="contact-submitted-card">
                <CheckCircle2 size={56} className="mx-auto mb-4" style={{ color: "var(--accent-mint)" }} />
                <h3 className="display-md mb-3">Message received.</h3>
                <p className="text-[color:var(--muted)] max-w-md mx-auto">
                  We&apos;ll reply to {form.email} within one business day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="card space-y-5" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="field-label">Name *</label>
                    <input required className="field-input" value={form.name} onChange={update("name")} data-testid="contact-field-name" />
                  </div>
                  <div>
                    <label className="field-label">Email *</label>
                    <input required type="email" className="field-input" value={form.email} onChange={update("email")} data-testid="contact-field-email" />
                  </div>
                  <div>
                    <label className="field-label">Phone (optional)</label>
                    <input className="field-input" value={form.phone} onChange={update("phone")} data-testid="contact-field-phone" />
                  </div>
                  <div>
                    <label className="field-label">Inquiry type</label>
                    <select className="field-select" value={form.inquiry_type} onChange={update("inquiry_type")} data-testid="contact-field-inquiry_type">
                      <option value="general">General inquiry</option>
                      <option value="employer">I want to hire talent</option>
                      <option value="candidate">I&apos;m a candidate</option>
                      <option value="partnership">Partnership</option>
                      <option value="press">Press / media</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="field-label">Subject *</label>
                  <input required className="field-input" value={form.subject} onChange={update("subject")} data-testid="contact-field-subject" />
                </div>
                <div>
                  <label className="field-label">Message *</label>
                  <textarea required rows={5} className="field-textarea" value={form.message} onChange={update("message")} data-testid="contact-field-message" />
                </div>
                <button type="submit" disabled={submitting} className="btn-primary" data-testid="contact-submit-btn">
                  {submitting ? "Sending…" : "Send message"} <Send size={16} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
